import {
  OrderStatus,
  DeliveryType,
  PaymentMethod,
  PaymentStatus,
  Unit,
  ComplexOrderStatus,
  LegalEntityType,
  DeliveryStatus,
} from "../../types/enums";
import { getDeliveryTypeIcon } from "./enumToIcon";

// Функции для преобразования enum в текст
export const getOrderStatusText = (status: OrderStatus): string => {
  switch (Number(status)) {
    case Number(OrderStatus.NEW):
      return "Новый";
    case Number(OrderStatus.PREPARING):
      return "В работе";
    case Number(OrderStatus.DELIVERING):
      return "Доставляется";
    case Number(OrderStatus.DELIVERED):
      return "Доставлен";
    case Number(OrderStatus.PICKED_UP):
      return "Выдан при самовывозе";
    case Number(OrderStatus.CANCELLED):
      return "Отменен";
    default:
      return "Неизвестно";
    case OrderStatus.READY:
      return "Готов";
  }
};

export const getDeliveryTypeText = (type: DeliveryType): string => {
  switch (Number(type)) {
    case Number(DeliveryType.DELIVERY):
      return "Доставка";
    case Number(DeliveryType.PICKUP):
      return "Самовывоз";
    default:
      return "Неизвестно";
  }
};

export const getPaymentMethodText = (method: PaymentMethod): string => {
  switch (Number(method)) {
    case Number(PaymentMethod.CARD_ON_DELIVERY):
      return "Картой при получении";
    case Number(PaymentMethod.CASH_ON_DELIVERY):
      return "Наличными при получении";
    case Number(PaymentMethod.ONLINE):
      return "Онлайн";
    default:
      return "Неизвестно";
  }
};

export const getPaymentStatusText = (status: PaymentStatus): string => {
  switch (Number(status)) {
    case Number(PaymentStatus.PENDING):
      return "Ожидает оплаты";
    case Number(PaymentStatus.PAID):
      return "Оплачен";
    case Number(PaymentStatus.FAILED):
      return "Ошибка оплаты";
    case Number(PaymentStatus.REFUNDED):
      return "Возврат";
    default:
      return "Неизвестно";
  }
};

// Функции для получения цвета статуса
export const getOrderStatusColor = (status: OrderStatus): string => {
  switch (Number(status)) {
    case Number(OrderStatus.NEW):
      return "#3498db"; // синий
    case Number(OrderStatus.PREPARING):
      return "#f39c12"; // оранжевый
    case Number(OrderStatus.DELIVERING):
      return "#9b59b6"; // фиолетовый
    case Number(OrderStatus.DELIVERED):
      return "#2ecc71"; // зеленый
    case Number(OrderStatus.PICKED_UP):
      return "#27ae60"; // темно-зеленый
    case Number(OrderStatus.CANCELLED):
      return "#e74c3c"; // красный
    default:
      return "#95a5a6"; // серый
  }
};

export const getPaymentStatusColor = (status: PaymentStatus): string => {
  switch (Number(status)) {
    case Number(PaymentStatus.PENDING):
      return "#f39c12"; // оранжевый
    case Number(PaymentStatus.PAID):
      return "#2ecc71"; // зеленый
    case Number(PaymentStatus.FAILED):
      return "#e74c3c"; // красный
    case Number(PaymentStatus.REFUNDED):
      return "#95a5a6"; // серый
    default:
      return "#95a5a6"; // серый
  }
};

// Функции для получения всех значений enum в формате для селекторов
export const getOrderStatusOptions = () => {
  return Object.values(OrderStatus)
    .filter((value) => typeof value === "number")
    .map((status) => ({
      value: status as OrderStatus,
      label: getOrderStatusText(status as OrderStatus),
      color: getOrderStatusColor(status as OrderStatus),
    }));
};

export const getDeliveryTypeOptions = () => {
  return Object.values(DeliveryType)
    .filter((value) => typeof value === "number")
    .map((type) => ({
      value: type as DeliveryType,
      label: getDeliveryTypeText(type as DeliveryType),
      icon: getDeliveryTypeIcon(type as DeliveryType),
    }));
};

export const getPaymentMethodOptions = () => {
  return Object.values(PaymentMethod)
    .filter((value) => typeof value === "number")
    .map((method) => ({
      value: method as PaymentMethod,
      label: getPaymentMethodText(method as PaymentMethod),
    }));
};

export const getPaymentStatusOptions = () => {
  return Object.values(PaymentStatus)
    .filter((value) => typeof value === "number")
    .map((status) => ({
      value: status as PaymentStatus,
      label: getPaymentStatusText(status as PaymentStatus),
      color: getPaymentStatusColor(status as PaymentStatus),
    }));
};

export const unitTypeToText = (value: Unit): string => {
  switch (value) {
    case Unit.NOT_SELECTED:
      return "Единица измерения";
    case Unit.CENTIMETER:
      return "сантиметр";
    case Unit.MILLILITER:
      return "миллиметр";
    case Unit.GRAM:
      return "грамм";
    case Unit.KILOGRAM:
      return "килограмм";
    case Unit.METER:
      return "метр";
    case Unit.LITER:
      return "литр";
    case Unit.PIECE:
      return "штука";
    case Unit.TON:
      return "тонна";
    case Unit.MINUTE:
      return "минута";
    case Unit.HOUR:
      return "час";
    case Unit.DAY:
      return "день";
    case Unit.MONTH:
      return "месяц";
    case Unit.YEAR:
      return "год";
    case Unit.PORTION:
      return "порция";
    case Unit.EMPTY:
      return "";

    default:
      return "Тип не найден";
  }
};

export const unitTypeToShort = (value: Unit): string => {
  switch (value) {
    case Unit.GRAM:
      return "г";
    case Unit.KILOGRAM:
      return "кг";
    case Unit.TON:
      return "т";
    case Unit.MILLILITER:
      return "мл";
    case Unit.LITER:
      return "л";
    case Unit.PIECE:
      return "шт";
    case Unit.CENTIMETER:
      return "см";
    case Unit.METER:
      return "м";
    case Unit.MINUTE:
      return "мин";
    case Unit.HOUR:
      return "ч";
    case Unit.DAY:
      return "дн";
    case Unit.MONTH:
      return "мес";
    case Unit.YEAR:
      return "год";
    case Unit.PORTION:
      return "порц";
    case Unit.EMPTY:
      return "";
    case Unit.NOT_SELECTED:
    default:
      return "";
  }
};

export const getStatusColor = (status: OrderStatus) => {
  switch (Number(status)) {
    case OrderStatus.NEW:
      return "rgba(52, 152, 219, 0.2)";
    case OrderStatus.PREPARING:
      return "rgba(243, 156, 18, 0.2)";
    case OrderStatus.DELIVERING:
      return "rgba(155, 89, 182, 0.2)";
    case OrderStatus.DELIVERED:
      return "rgba(46, 204, 113, 0.2)";
    case OrderStatus.PICKED_UP:
      return "rgba(39, 174, 96, 0.2)";
    case OrderStatus.CANCELLED:
      return "rgba(231, 76, 60, 0.2)";
    default:
      return "rgba(149, 165, 166, 0.2)";
  }
};

export const getStatusTextColor = (status: OrderStatus) => {
  switch (Number(status)) {
    case OrderStatus.NEW:
      return "#3498db";
    case OrderStatus.PREPARING:
      return "#f39c12";
    case OrderStatus.DELIVERING:
      return "#9b59b6";
    case OrderStatus.DELIVERED:
      return "#2ecc71";
    case OrderStatus.PICKED_UP:
      return "#27ae60";
    case OrderStatus.CANCELLED:
      return "#e74c3c";
    default:
      return "#95a5a6";
  }
};

export const getComplexOrderStatusColor = (
  status: ComplexOrderStatus,
): string => {
  switch (status) {
    case ComplexOrderStatus.DRAFT:
      return "rgba(52, 152, 219, 0.2)";
    case ComplexOrderStatus.SENT_TO_MANAGER:
      return "rgba(52, 152, 219, 0.2)";
    case ComplexOrderStatus.IN_PROGRESS:
      return "rgba(243, 156, 18, 0.2)";
    case ComplexOrderStatus.APPROVED:
      return "rgba(46, 204, 113, 0.2)";
    case ComplexOrderStatus.CONVERTED:
      return "rgba(46, 204, 113, 0.2)";
    case ComplexOrderStatus.ARCHIVED:
      return "rgba(149, 165, 166, 0.2)";
    default:
      return "rgba(149, 165, 166, 0.2)";
  }
};

export const getComplexOrderStatusTextColor = (
  status: ComplexOrderStatus,
): string => {
  switch (status) {
    case ComplexOrderStatus.DRAFT:
      return "#3498db";
    case ComplexOrderStatus.SENT_TO_MANAGER:
      return "#3498db";
    case ComplexOrderStatus.IN_PROGRESS:
      return "#f39c12";
    case ComplexOrderStatus.APPROVED:
      return "#2ecc71";
    case ComplexOrderStatus.CONVERTED:
      return "#2ecc71";
    case ComplexOrderStatus.ARCHIVED:
      return "#95a5a6";
    default:
      return "#95a5a6";
  }
};

export const getLegalEntityPrefix = (type?: LegalEntityType): string => {
  switch (type) {
    case LegalEntityType.INDIVIDUAL:
      return "ИП";
    case LegalEntityType.LLC:
      return "ООО";
    case LegalEntityType.JSC:
      return "АО";
    case LegalEntityType.SOLE:
      return "Самозанятый";
    case LegalEntityType.OTHER:
      return "";
    default:
      return "";
  }
};

export const getDeliveryStatusText = (status?: DeliveryStatus): string => {
  if (!status) return "Обрабатывается";

  const statusMap: Record<DeliveryStatus, string> = {
    [DeliveryStatus.CREATED]: "Создан",
    [DeliveryStatus.IN_TRANSIT]: "В пути",
    [DeliveryStatus.READY_FOR_PICKUP]: "Готов к выдаче",
    [DeliveryStatus.DELIVERED]: "Доставлен",
    [DeliveryStatus.NOT_DELIVERED]: "Не доставлен",
    [DeliveryStatus.RETURNED]: "Возвращён",
    [DeliveryStatus.CANCELLED]: "Отменён",
  };

  return statusMap[status] || status;
};
